import { socket } from '@/api/socket';
import ClientBalancesList from '@/components/client/client-balances-list';
import ClientCommandsList from '@/components/client/client-commands-list';
import ClientFeedTabs, { type ClientFeedTab } from '@/components/client/client-feed-tabs';
import ClientMeta from '@/components/client/client-meta';
import ClientSmsList from '@/components/client/client-sms-list';
import { getCommandTypeInfo } from '@/components/client/command-type-info';
import EntityHeroCard, { OnlineStatusBadge } from '@/components/navigation/entity-hero-card';
import { Spinner } from '@/components/ui/spinner';
import {
	$clientDetailActions,
	$clientDetailBalances,
	$clientDetailMessages,
	$clients,
	$selectedClient,
	$setClientDetailActions,
	$setClientDetailBalances,
	$setClientDetailCodes,
	$setClientDetailMessages,
	$setClientDetailApps,
	$setSelectedClient,
} from '@/entities/client.entity';
import { PageScroll } from '@/layouts/page-layout';
import { isDevMode } from '@/lib/is-dev-mode';
import { getMockActionsForClient, getMockBalancesForClient, getMockCodesForClient, getMockMessagesForClient } from '@/mocks/fixtures';
import { useUnit } from 'effector-react';
import { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router';
import { toast } from 'sonner';

export default function ClientPage() {
	const params = useParams();
	const [
		client,
		setSelectedClient,
		allBalances,
		allMessages,
		allActions,
		clients,
		setClientDetailBalances,
		setClientDetailCodes,
		setClientDetailMessages,
		setClientDetailActions,
		setClientDetailApps,
	] = useUnit([
		$selectedClient,
		$setSelectedClient,
		$clientDetailBalances,
		$clientDetailMessages,
		$clientDetailActions,
		$clients,
		$setClientDetailBalances,
		$setClientDetailCodes,
		$setClientDetailMessages,
		$setClientDetailActions,
		$setClientDetailApps,
	]);
	const [listType, setListType] = useState<ClientFeedTab>('balances');
	const [searchText, setSearchText] = useState('');

	const isSearching = searchText.trim().length > 0;

	useEffect(() => {
		return () => {
			setSelectedClient(null);
			setClientDetailApps([]);
		};
	}, [setSelectedClient, setClientDetailApps]);

	useEffect(() => {
		if (!params.id) return;

		if (isDevMode) {
			const found = clients.find(c => c.id === params.id);
			if (found) {
				setSelectedClient(found);
				setClientDetailBalances(getMockBalancesForClient(found.id));
				setClientDetailCodes(getMockCodesForClient(found.id));
				setClientDetailMessages(getMockMessagesForClient(found.id));
				setClientDetailActions(getMockActionsForClient(found.id));
			}
			return;
		}

		if (!socket) return;
		socket.emit('get-client', params.id);
	}, [params, clients, setSelectedClient, setClientDetailBalances, setClientDetailCodes, setClientDetailMessages, setClientDetailActions]);

	const [balances, messages, actions] = useMemo(() => {
		if (!isSearching) return [allBalances, allMessages, allActions];

		const q = searchText.toLowerCase();
		const filteredBalances = allBalances.filter(
			log =>
				log.text.toLowerCase().includes(q) ||
				log.sender.toLowerCase().includes(q) ||
				(log.cardNumber && log.cardNumber.toLowerCase().includes(q)),
		);
		const filteredMessages = allMessages.filter(log => log.text.toLowerCase().includes(q) || log.sender.toLowerCase().includes(q));
		const filteredActions = allActions.filter(action => {
			const typeLabel = getCommandTypeInfo(action).label.toLowerCase();
			const text = action.params?.text?.toLowerCase() ?? '';
			const phone = action.params?.phone?.toLowerCase() ?? '';
			return typeLabel.includes(q) || text.includes(q) || phone.includes(q) || action.type.toLowerCase().includes(q);
		});

		return [filteredBalances, filteredMessages, filteredActions];
	}, [allBalances, allMessages, allActions, searchText, isSearching]);

	function handleCopy(text: string) {
		navigator.clipboard
			.writeText(text)
			.then(() => toast.success('Скопировано'))
			.catch(() => toast.error('Ошибка копирования'));
	}

	if (!client) {
		return (
			<div className='absolute inset-0 flex items-center justify-center'>
				<Spinner className='w-[50px] h-[50px]' />
			</div>
		);
	}

	const feedCounts = {
		balances: allBalances.length,
		sms: allMessages.length,
		commands: allActions.length,
	};

	const heroAccent = client.takeStatus === 1 ? 'take' : client.isOnline ? 'online' : 'offline';

	return (
		<PageScroll contentClassName='p-2 flex flex-col gap-2'>
			<EntityHeroCard
				accent={heroAccent}
				title={`#${client.id}`}
				subtitle={client.takeStatus === 1 ? `На вбиве — ${client.takenFrom}` : undefined}
				trailing={<OnlineStatusBadge isOnline={client.isOnline} />}
			/>
			<ClientMeta client={client} onCopy={handleCopy} />
			<div className='sticky top-0 z-10 -mx-2 px-2 py-2 bg-background/95 backdrop-blur-sm'>
				<ClientFeedTabs
					listType={listType}
					onListTypeChange={setListType}
					searchText={searchText}
					onSearchChange={setSearchText}
					counts={feedCounts}
				/>
			</div>
			{listType === 'balances' && <ClientBalancesList balances={balances} onCopy={handleCopy} isSearching={isSearching} />}
			{listType === 'sms' && <ClientSmsList messages={messages} onCopy={handleCopy} isSearching={isSearching} />}
			{listType === 'commands' && <ClientCommandsList actions={actions} onCopy={handleCopy} />}
		</PageScroll>
	);
}
